import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { AdminProfileErrorCode } from '~src/commonInfra/utils/ErrorCodes';

import {
    AdminProfileDialogBody,
    AdminProfileDialogProps,
    AdminProfileDialogState,
} from '../components/AdminProfileDialog';
import { IAdminProfileTemplate } from '../models/IAdminProfileTemplate';
import { ICreateAdminProfileResult } from '../requests/CreateAdminProfileRequest';
import { AdminProfilesService } from './AdminProfilesService';

const DIALOG_WIDTH = 1200 as TabDialogWidth;

const initialDialogState: AdminProfileDialogState = {
    name: '',
    description: '',
    selectedPermissions: [],
};

function isActionDisabled(bodyProps: AdminProfileDialogProps, state: AdminProfileDialogState): boolean {
    if (!state.name.trim() || bodyProps.isLoadingTemplate) {
        return true;
    }
    return bodyProps.existingProfileNames.some(
        existing => existing.toLowerCase() === state.name.trim().toLowerCase()
    );
}

export function showCreateAdminProfileDialog(
    template: IAdminProfileTemplate | null,
    existingProfileNames: string[],
    onSuccess: (luid: string) => void
): void {
    const initialBodyProps: AdminProfileDialogProps = {
        template,
        isLoadingTemplate: false,
        existingProfileNames,
    };

    openReactActionDialog<AdminProfileDialogProps, AdminProfileDialogState, ICreateAdminProfileResult>({
        title: Messages.createAdminProfileDialogTitle(),
        bodyComponent: AdminProfileDialogBody,
        initialBodyProps,
        initialDialogState,
        actionName: (_bodyProps, dialogState) => {
            const count = dialogState.selectedPermissions.length;
            return count > 0
                ? Messages.createAdminProfileActionWithCount({ COUNT: String(count) })
                : Messages.createAdminProfileDialogAction();
        },
        actionDisabled: isActionDisabled,
        action: createAdminProfileAction(onSuccess),
        dismissDialogOnActionFailed: false,
        dialogWidth: DIALOG_WIDTH,
    });
}

function createAdminProfileAction(onSuccess: (luid: string) => void) {
    return async (
        _bodyProps: AdminProfileDialogProps,
        dialogState: AdminProfileDialogState,
        updateDialogState: React.Dispatch<MergeOrUpdateStateAction<AdminProfileDialogState>>
    ): Promise<ICreateAdminProfileResult> => {
        const result = await AdminProfilesService.createAdminProfile({
            name: dialogState.name,
            description: dialogState.description,
            permissions: dialogState.selectedPermissions.map(p => ({
                capability: p.capability,
                resource: p.category,
            })),
        });
        return notify(updateDialogState, result, onSuccess);
    };
}

function notify(
    updateDialogState: React.Dispatch<MergeOrUpdateStateAction<AdminProfileDialogState>>,
    result: ICreateAdminProfileResult,
    onSuccess: (luid: string) => void
): Promise<ICreateAdminProfileResult> {
    if (!!result.errors && result.errors.length > 0) {
        const errorMsg: string = errorMessageFor(Number(result.errors[0].code));
        if (errorMsg) {
            updateDialogState({ errorMessage: errorMsg });
            return Promise.reject();
        } else {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.createAdminProfileCreateFailed() });
            return Promise.resolve(result);
        }
    } else {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.createAdminProfileDialogTitle() });
        onSuccess(result.luid);
        return Promise.resolve(result);
    }
}

function errorMessageFor(errorCode: number): string {
    switch (errorCode) {
        case AdminProfileErrorCode.InvalidName:
            return Messages.createAdminProfileInvalidName();
        default:
            return undefined;
    }
}

export const CreateAdminProfileDialogAction = {
    notify,
    isActionDisabled,
    errorMessageFor,
};

export type CreateAdminProfileDialogAction = typeof CreateAdminProfileDialogAction;
